import React, { ChangeEvent, useState } from 'react'
import { Select, TextInput, Typography } from '@telavita-core/react-design-kit'

import { GENDER } from 'settings/_personSettings'

import { useFieldCPFValidator } from './hooks/useFieldCPFValidator'
import { useFieldDateBirthValidator } from './hooks/useFieldDateBirthValidator'
import { useUpdatePatientInfo } from './hooks/useUpdatePatientInfo'

import ModalGeneric from '../ModalGeneric'
import { ReactComponent as ExclamationIcon } from 'static/svg/exclamation-icon.svg'

import { Container, ErrorMessageWrapper, FieldWrapper, Text } from './styles'

interface RequiredPatientInfoModalProps {
  onClose?: () => void;
  title: string;
  patientInfo: {
    cpf: string;
    dateBirth: string;
    gender: string;
  }
  isFormFulfillmentObligatory: boolean;
}

type Gender = {
  id: number;
  label: string;
  code: string;
}

const ErrorMessageWithIcon = ({ message }: { message: string }) => (
  <ErrorMessageWrapper>
    <ExclamationIcon />
    <Typography 
      variant='content3' 
      color='danger' 
      inlineStyles={{maxWidth: '90%', marginLeft: '10px'}}
    >
      {message}
    </Typography>
  </ErrorMessageWrapper>
)

export const RequiredPatientInfoModal = ({ 
  onClose, 
  title,
  patientInfo,
  isFormFulfillmentObligatory
}: RequiredPatientInfoModalProps): JSX.Element => {
  const [cpf, setCpf] = useState<string | null>(null)
  const [dateBirth, setDateBirth] = useState<string | null>(null)
  const [gender, setGender] = useState<Gender | null>(null)

  const { 
    isCPFValid, 
    errorMessageToCPF,
    errorMessageToAlreadyRegisteredCPF
  } = useFieldCPFValidator(cpf)
  const { fieldMessageToDateBirth, isDateBirthValid } = useFieldDateBirthValidator(dateBirth)
  const { updateInfo, loading, success } = useUpdatePatientInfo()

  const handleUpdatePatientInfo = () => 
    void updateInfo({
      cpf,
      dateBirth,
      gender: gender?.code
    })

  const handleShouldEnableButton = () => {
    const patient = {
      cpf: patientInfo?.cpf || isCPFValid,
      dateBirth: patientInfo?.dateBirth || isDateBirthValid,
      gender: patientInfo?.gender || gender?.code
    }

    return Object.values(patient).every(field => !!field)
  }
  
  const genderOptions = GENDER
    .map(item => ({
      id: item.id,
      label: item.name,
      code: item.code
    }))

  function handleSelectGender(selectedGender: Gender) {
    setGender(prevState => ({
      ...prevState,
      id: selectedGender.id,
      label: selectedGender.label,
      code: selectedGender.code
    }))
  }

  return (
    <ModalGeneric
      feedbackShow={success}
      feedbackMessage='Seu cadastro foi completado com sucesso!' 
      feedbackType='success'
      modalTitle={title}
      modalSubtitle="Estes novos dados são importantes para que o profissional de saúde tenha mais informações sobre você."
      hasButton
      hasCloseButton={!isFormFulfillmentObligatory}
      btnText='ENVIAR'
      btnLoading={loading}
      onCloseModal={onClose}
      btnOnClick={handleUpdatePatientInfo}
      btnDisabled={!handleShouldEnableButton()}
      hasLink={!isFormFulfillmentObligatory}
      linkText="Mais Tarde"
      onClickLink={onClose}
    >
      <Container>
        <Typography variant="content2">
          Preencha os campos abaixo:
        </Typography>
           
        <FieldWrapper>
          {!patientInfo?.cpf ? (
            <div>
              <TextInput
                hasError={!!errorMessageToCPF || !!errorMessageToAlreadyRegisteredCPF}
                messageError={errorMessageToCPF}
                name="cpf"
                onInputChange={(e: ChangeEvent<HTMLInputElement>) => setCpf(e.target.value)}
                placeholder="CPF*"
                required
                type="text"
                value={cpf}
                mask='111.111.111-**'
              />

              {errorMessageToAlreadyRegisteredCPF && 
                <ErrorMessageWithIcon message={errorMessageToAlreadyRegisteredCPF} />
              }
            </div>
          ): null}

          {!patientInfo?.dateBirth ? (
            <TextInput
              value={dateBirth}
              hasError={!!fieldMessageToDateBirth}
              messageError={fieldMessageToDateBirth}
              name="dateBirth"
              mask="11/11/1111"
              placeholder="Data de nascimento*"
              onInputChange={(e: ChangeEvent<HTMLInputElement>) => setDateBirth(e.target.value)}
              required
            />
          ): null}

          {!patientInfo.gender ? (
            <Select
              label={null}
              placeholder='Gênero*'
              selected={gender}
              onSelect={handleSelectGender}
              options={genderOptions}
              customClassName="customSelect"
            />
          ) : null}
        </FieldWrapper>

        <Text variant='content2'>
          * Campos obrigatórios
        </Text>
      </Container>
    </ModalGeneric>
  )
}